import { compileMatrix, createEmptyAnswers, type CompiledForm } from '@record-fill';
import {
  buildRecordPdfFileName,
  recordTitleOf,
  type GeneratedRecordForm,
} from '@/entities/GeneratedRecord';

export const SHEET_EXPORT_ERROR_TITLE = 'Não foi possível exportar o PDF. Tente novamente.';

// Read only shows no issue, so no pattern needs testing: a hostile pattern would only block the
// main thread for a verdict nobody sees.
const skipPatternTest = () => true;

export function compileExportSheet(form: GeneratedRecordForm): CompiledForm {
  return compileMatrix({
    structure: form.structure,
    rules: form.rules,
    permissions: form.permissions,
    subject: form.subject,
    answers: { ...createEmptyAnswers(form.matrix.id), ...form.answers },
    options: { patternTest: skipPatternTest },
    titles: form.titles,
  });
}

export interface SheetPdfNaming {
  fileName: string;
  title: string;
}

export function buildSheetPdfNaming(form: GeneratedRecordForm): SheetPdfNaming | null {
  const record = form.record;
  if (!record) return null;
  return {
    fileName: buildRecordPdfFileName(record),
    title: recordTitleOf(record),
  };
}

// The capture is a picture of the stage, and a control shows only what fits: a paragraph its 7
// rows, a list its first line with an ellipsis, a text field the start of its value. The stage is
// thrown away after the capture, so it is opened up here and never restored.
export function revealClippedText(stage: HTMLElement): void {
  for (const textarea of stage.querySelectorAll('textarea')) {
    if (textarea.scrollHeight <= textarea.clientHeight) continue;
    const borders = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + borders}px`;
  }
  // A placeholder is no answer, and a single word cannot wrap: it would run under the arrow.
  for (const label of stage.querySelectorAll<HTMLElement>(
    '.truncate:not([data-select-placeholder])'
  )) {
    if (label.scrollWidth <= label.clientWidth) continue;
    label.style.whiteSpace = 'normal';
    label.style.overflowWrap = 'anywhere';
    label.style.textOverflow = 'clip';
    label.style.overflow = 'visible';
  }
  // An input never wraps, so its value goes to a block with the same look, placed beside it: Vue
  // still owns the input and removes it with the stage.
  const wrappable = 'input[type="text"], input[type="url"], input[type="email"]';
  for (const input of stage.querySelectorAll<HTMLInputElement>(wrappable)) {
    if (input.scrollWidth <= input.clientWidth) continue;
    const text = document.createElement('div');
    text.className = input.className;
    text.textContent = input.value;
    text.style.whiteSpace = 'pre-wrap';
    text.style.overflowWrap = 'anywhere';
    text.style.height = 'auto';
    text.style.minHeight = `${input.offsetHeight}px`;
    input.style.display = 'none';
    input.after(text);
  }
}
